import { type FC, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { useChainNames } from 'dibs-crypto-utils/exports/useChainNames.ts';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { WithWalletConnectionEnsuredModalBase } from './WithWalletConnectionEnsuredModalBase.tsx';
import { type UnsupportedChainContent_viewer$key } from './__generated__/UnsupportedChainContent_viewer.graphql.ts';

export const UnsupportedChainContent: FC<{
    viewer: UnsupportedChainContent_viewer$key;
    isMetaMaskInstalled: boolean;
    switchMetaMaskEthereumChain: () => Promise<void>;
}> = ({ viewer: viewerRef, isMetaMaskInstalled, switchMetaMaskEthereumChain }) => {
    useEffect(() => {
        trackEvent({
            event: 'e_ga_event',
            category: 'NFT',
            action: 'ethereum error displayed',
            label: 'unsupported chain',
        });
    }, []);

    const viewer = useFragment(
        graphql`
            fragment UnsupportedChainContent_viewer on Viewer {
                ...WithWalletConnectionEnsuredModalBase_viewer
            }
        `,
        viewerRef
    );

    const { requiredNetworks, connectedNetwork, isLocalHost } = useChainNames();

    return (
        <WithWalletConnectionEnsuredModalBase
            viewer={viewer}
            title={
                <FormattedMessage
                    id="nft.withWalletConnection.unsupportedNetwork.header"
                    defaultMessage="Unsupported Network"
                />
            }
            warning={
                isMetaMaskInstalled ? (
                    <FormattedMessage
                        id="nft.withWalletConnection.unsupportedNetwork.metaMask.warning"
                        defaultMessage="You are on {connectedNetwork} network, <a>please switch to {requiredNetworks}</a>."
                        values={{
                            connectedNetwork,
                            requiredNetworks,
                            a: chunk => (
                                <Link
                                    onClick={switchMetaMaskEthereumChain}
                                    key="switchMetaMaskEthereumChain"
                                >
                                    {chunk}
                                </Link>
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="nft.withWalletConnection.unsupportedNetwork.walletConnect.warning"
                        defaultMessage="You are on {connectedNetwork} network, but should be connected to {requiredNetworks}."
                        values={{
                            connectedNetwork,
                            requiredNetworks,
                        }}
                    />
                )
            }
            paragraphs={[
                <FormattedMessage
                    id="nft.withWalletConnection.unsupportedNetwork.bodyCopy1"
                    defaultMessage="Please connect to {requiredNetworks} in order to place your bid."
                    values={{
                        requiredNetworks,
                    }}
                />,
                isLocalHost
                    ? "Localhost is not supported if you aren't running 1stibs-contracts locally."
                    : null,
            ]}
            showWalletButtons={false}
        />
    );
};
